import { ExistingParticipationRecordPage } from './pages/participation-limitations/ExistingParticipationRecordPage';
import { ParticipationLimitationPage } from './pages/participation-limitations/ParticipationLimitationPage';
import { EventSubstitutionPage } from './pages/event-substitutions/EventSubstitutionPage';
import { FitnessAssessmentPage } from './pages/fitness-assessments/FitnessAssessmentPage';
import { LanePreparationPage } from './pages/lane-assignments/LanePreparationPage';
import { MeasurementReleasePage } from './pages/measurement-release/MeasurementReleasePage';
import { HumanReviewPage } from './pages/human-review/HumanReviewPage';
import { OutcomeLinkPage } from './pages/outcome-links/OutcomeLinkPage';
import { IntegrationReconciliationPage } from './pages/integrations/IntegrationReconciliationPage';
import { IntakeSupervisorPage } from './pages/capture/IntakeSupervisorPage';
import { LearnerEvidencePage } from './pages/evidence/LearnerEvidencePage';
import { ConfigurationExecutionPage } from './pages/configuration/ConfigurationExecutionPage';
import { TeamCapturePage } from './pages/capture/TeamCapturePage';
import { CaptureGovernancePage } from './pages/capture/CaptureGovernancePage';
import { CadreSurveyIntakePage } from './pages/capture/CadreSurveyIntakePage';
import { ConfigurationPage } from './pages/configuration/ConfigurationPage';
import { useSyncExternalStore } from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route, useParams } from 'react-router-dom';
import dynamoProvider from './providers/dynamo';
import cognitoProvider, {
  setDataProviderRef, getSessionRevision, subscribeSession,
} from './providers/cognito';
import { theme } from './theme';
import CustomLayout from './components/Layout';

import Dashboard, { AnalyticsBoundary } from './components/Dashboard';
import Analytics from './components/Analytics';
import { ConsentModal } from './components/ConsentModal';
import { OnboardingWizard } from './components/OnboardingWizard';
import { LoginPage } from './pages/LoginPage';
import { RedeemPage } from './pages/RedeemPage';

import { courses } from './resources/courses';
import { assessments } from './resources/assessments';
import { instances } from './resources/assessment_instances';
import { students } from './resources/students';
import { student_groups } from './resources/student_groups';
import { surveys } from './resources/surveys';
import { assignments } from './resources/assignments';
import { rankings } from './resources/rankings';
import { taxonomies, taxonomy_terms } from './resources/taxonomy';
import { mos_profiles } from './resources/mos-profiles';
import { CohortKsbView } from './resources/cohort-ksb-view';
import { CohortOutcomesView } from './resources/cohort-outcomes-view';
import { InterRaterView } from './resources/inter-rater-view';
import { CalibrationConsensusView } from './resources/calibration-consensus-view';
import { PhaseBoardView } from './resources/phase-board-view';
import { OrgUnitList, OrgUnitCreate, OrgUnitEdit, OrgUnitShow } from './resources/org-units';
import {
  InstructorList,
  InstructorCreate,
  InstructorEdit,
  InstructorShow,
  InstructorUtilization,
} from './resources/instructors';
import {
  ProgramList,
  ProgramCreate,
  ProgramEdit,
  ProgramShow,
  ProgramEffectiveness,
} from './resources/programs';
import { attempts } from './resources/attempts';
import { teams } from './resources/teams';
import { disqualifications } from './resources/disqualifications';
import { events } from './resources/events';
import { score_tables } from './resources/score-tables';
import { users } from './resources/users';
import {
  LicenseList,
  LicenseShow,
  LicenseAdminPage,
} from './resources/licenses';
import { DataExchangePage } from './resources/data-exchange';
import { BatchGradeGrid } from './resources/batch-grade';
import {
  WearableSettingsPage,
  ConsentDashboardPage,
  BiometricImportPage,
} from './resources/wearables';

import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import TableChartIcon from '@mui/icons-material/TableChart';
import BlockIcon from '@mui/icons-material/Block';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PeopleIcon from '@mui/icons-material/People';
import QuizIcon from '@mui/icons-material/Quiz';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import BadgeIcon from '@mui/icons-material/Badge';
import GroupsIcon from '@mui/icons-material/Groups';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChecklistIcon from '@mui/icons-material/Checklist';

// Wire up the data provider reference for auth
setDataProviderRef(dynamoProvider);

// Route wrapper — reads :id from URL and passes it to ProgramEffectiveness
const ProgramEffectivenessRoute = () => {
  const { id } = useParams<{ id: string }>();
  return <ProgramEffectiveness programId={id ?? ''} />;
};
ProgramEffectivenessRoute.displayName = 'ProgramEffectivenessRoute';

const App = () => {
  const session = useSyncExternalStore(subscribeSession, getSessionRevision, () => 0);
  return (
    <>
      <Admin
        key={session}
        disableTelemetry
        dashboard={Dashboard}
        layout={CustomLayout}
        authProvider={cognitoProvider}
        dataProvider={dynamoProvider}
        theme={theme}
        loginPage={LoginPage}
      >
        <Resource
          name="courses"
          options={{ label: 'Courses' }}
          icon={SchoolIcon}
          {...courses}
        />
        <Resource
          name="surveys"
          options={{ label: 'Pre-Course Surveys' }}
          icon={QuizIcon}
          {...surveys}
        />
        <Resource
          name="assessments"
          options={{ label: 'Assessments' }}
          icon={AssignmentIcon}
          {...assessments}
        />
        <Resource
          name="events"
          options={{ label: 'Assessment Events' }}
          icon={EventIcon}
          {...events}
        />
        <Resource
          name="instances"
          options={{ label: 'Assessment Schedule' }}
          icon={CalendarMonthIcon}
          {...instances}
        />
        <Resource
          name="student_groups"
          options={{ label: 'Classes' }}
          icon={GroupWorkIcon}
          {...student_groups}
        />
        <Resource
          name="students"
          options={{ label: 'Students' }}
          icon={PeopleIcon}
          {...students}
        />
        <Resource
          name="teams"
          options={{ label: 'Teams' }}
          icon={GroupsIcon}
          {...teams}
        />
        <Resource
          name="assignments"
          options={{ label: 'Student Assignments' }}
          icon={ListAltIcon}
          {...assignments}
        />
        <Resource
          name="attempts"
          options={{ label: 'Student Event Attempts' }}
          icon={SportsScoreIcon}
          {...attempts}
        />
        <Resource
          name="score_tables"
          options={{ label: 'Scoring Tables' }}
          icon={TableChartIcon}
          {...score_tables}
        />
        <Resource
          name="disqualifications"
          options={{ label: 'Participation Records' }}
          icon={BlockIcon}
          {...disqualifications}
        />
        <Resource
          name="users"
          options={{ label: 'Staff' }}
          icon={PersonIcon}
          {...users}
        />
        <Resource
          name="rankings"
          options={{ label: 'Rankings' }}
          icon={MilitaryTechIcon}
          {...rankings}
        />
        <Resource
          name="taxonomies"
          options={{ label: 'Taxonomies' }}
          icon={AccountTreeIcon}
          {...taxonomies}
        />
        <Resource
          name="taxonomy_terms"
          options={{ label: 'Taxonomy Terms' }}
          icon={BadgeIcon}
          {...taxonomy_terms}
        />
        <Resource
          name="org_units"
          list={OrgUnitList}
          create={OrgUnitCreate}
          edit={OrgUnitEdit}
          show={OrgUnitShow}
          options={{ label: 'Org Units' }}
          icon={CorporateFareIcon}
        />
        <Resource
          name="instructors"
          list={InstructorList}
          create={InstructorCreate}
          edit={InstructorEdit}
          show={InstructorShow}
          options={{ label: 'Instructors' }}
          icon={GroupsIcon}
        />
        <Resource
          name="programs"
          list={ProgramList}
          create={ProgramCreate}
          edit={ProgramEdit}
          show={ProgramShow}
          options={{ label: 'Programs' }}
          icon={TrackChangesIcon}
        />
        <Resource
          name="licenses"
          list={LicenseList}
          show={LicenseShow}
          options={{ label: 'Licenses' }}
          icon={VerifiedIcon}
        />
        <Resource
          name="mos_profiles"
          options={{ label: 'MOS / AOC Profiles' }}
          icon={ChecklistIcon}
          {...mos_profiles}
        />
        <CustomRoutes noLayout>
          <Route path="/redeem" element={<RedeemPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path="/analytics"
            element={
              <AnalyticsBoundary>
                <Analytics />
              </AnalyticsBoundary>
            }
          />
          <Route
            path="/instructor-utilization"
            element={
              <AnalyticsBoundary>
                {(scope) =>
                  scope.kind === 'command' || scope.kind === 'cadre' ? (
                    <InstructorUtilization rootOrgPath={scope.orgPath} />
                  ) : (
                    <div role="alert">Access Denied</div>
                  )
                }
              </AnalyticsBoundary>
            }
          />
          <Route
            path="/program-effectiveness/:id"
            element={
              <AnalyticsBoundary>
                <ProgramEffectivenessRoute />
              </AnalyticsBoundary>
            }
          />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="/licensing" element={<LicenseAdminPage />} />
          <Route path="/data-exchange" element={<DataExchangePage />} />
          <Route path="/wearable-settings" element={<WearableSettingsPage />} />
          <Route path="/wearable-consents-admin" element={<ConsentDashboardPage />} />
          <Route path="/biometric-import" element={<BiometricImportPage />} />
          <Route path="/intake-supervisor" element={<IntakeSupervisorPage />} />
          <Route path="/integration-reconciliation" element={<IntegrationReconciliationPage />} />
          <Route path="/measurement-releases" element={<MeasurementReleasePage />} />
          <Route path="/fitness-assessments" element={<FitnessAssessmentPage />} />
          <Route path="/existing-participation-record" element={<ExistingParticipationRecordPage />} />
          <Route path="/participation-limitations" element={<ParticipationLimitationPage />} />
          <Route path="/event-substitutions" element={<EventSubstitutionPage />} />
          <Route path="/lane-preparation" element={<LanePreparationPage />} />
          <Route path="/human-review" element={<HumanReviewPage />} />
          <Route path="/outcome-links" element={<OutcomeLinkPage />} />
          <Route path="/learner-evidence" element={<LearnerEvidencePage />} />
          <Route path="/team-capture" element={<TeamCapturePage />} />
          <Route path="/capture-governance" element={<CaptureGovernancePage />} />
          <Route path="/cadre-survey-entry" element={<CadreSurveyIntakePage />} />
          <Route path="/configuration-executions" element={<ConfigurationExecutionPage />} />
          <Route path="/configurations" element={<ConfigurationPage />} />
          <Route path="/batch-grade" element={<BatchGradeGrid />} />
          <Route
            path="/cohort-ksb"
            element={
              <AnalyticsBoundary>
                {(scope) => <CohortKsbView rootOrgPath={scope.orgPath} />}
              </AnalyticsBoundary>
            }
          />
          <Route
            path="/cohort-outcomes"
            element={
              <AnalyticsBoundary>
                {(scope) => <CohortOutcomesView rootOrgPath={scope.orgPath} />}
              </AnalyticsBoundary>
            }
          />
          <Route
            path="/inter-rater"
            element={
              <AnalyticsBoundary>
                <InterRaterView />
              </AnalyticsBoundary>
            }
          />
          <Route
            path="/calibration-consensus"
            element={
              <AnalyticsBoundary>
                <CalibrationConsensusView />
              </AnalyticsBoundary>
            }
          />
          <Route
            path="/phase-board"
            element={
              <AnalyticsBoundary>
                <PhaseBoardView />
              </AnalyticsBoundary>
            }
          />
        </CustomRoutes>
      </Admin>
      <ConsentModal />
    </>
  );
};

export default App;
